import { useEffect, useState } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { createWsClient, type WsClient, type WsState } from "../lib/ws";

export type ConnectionStatus = "connecting" | "open" | "reconnecting" | "closed";

export type WorkspaceEvent = { type: string; payload: unknown };
export type EventListener = (e: WorkspaceEvent) => void;

interface RegistryEntry {
  client: WsClient;
  refCount: number;
  closeTimer: ReturnType<typeof setTimeout> | null;
  fanout: Set<EventListener>;
  off: () => void;
  status: ConnectionStatus;
  statusListeners: Set<(s: ConnectionStatus) => void>;
  pollTimer: ReturnType<typeof setInterval> | null;
}

const CLOSE_GRACE_MS = 5_000;
const STATUS_POLL_MS = 500;

const registry = new Map<string, RegistryEntry>();

function invalidatePlayDetail(
  qc: QueryClient,
  workspaceId: string,
  payload: unknown,
) {
  const p = (payload ?? {}) as Record<string, unknown>;
  const playId = typeof p["playId"] === "string" ? (p["playId"] as string) : null;
  if (playId) {
    qc.invalidateQueries({ queryKey: ["play", workspaceId, playId] });
  } else {
    // Coarse invalidate when payload doesn't carry playId.
    qc.invalidateQueries({
      predicate: (q) =>
        Array.isArray(q.queryKey) &&
        q.queryKey[0] === "play" &&
        q.queryKey[1] === workspaceId,
    });
  }
}

function fanoutEvent(workspaceId: string, qc: QueryClient, msg: WorkspaceEvent) {
  const t = msg.type;
  if (
    t === "play.state_changed" ||
    t === "play.approved" ||
    t === "play.started" ||
    t === "play.paused" ||
    t === "play.completed" ||
    t === "play.created" ||
    t === "play.abandoned"
  ) {
    qc.invalidateQueries({ queryKey: ["plays", workspaceId] });
    invalidatePlayDetail(qc, workspaceId, msg.payload);
  }
  if (
    t === "step.state_changed" ||
    t === "step.completed" ||
    t === "step.started" ||
    t === "step.failed" ||
    t === "step.claimed" ||
    t === "step.dispatched" ||
    t === "step.rejected" ||
    t === "play.step.completed" ||
    t === "play.step.started" ||
    t === "play.step.failed" ||
    t === "step.ready"
  ) {
    qc.invalidateQueries({ queryKey: ["me-steps"] });
    qc.invalidateQueries({ queryKey: ["plays", workspaceId] });
    invalidatePlayDetail(qc, workspaceId, msg.payload);
  }
  if (
    t === "replan.requested" ||
    t === "replan.applied" ||
    t === "replan.created" ||
    t === "replan.rejected"
  ) {
    qc.invalidateQueries({ queryKey: ["plays", workspaceId] });
    qc.invalidateQueries({ queryKey: ["replans", workspaceId] });
    invalidatePlayDetail(qc, workspaceId, msg.payload);
  }
  if (t === "decision.created" || t === "decision.resolved") {
    qc.invalidateQueries({ queryKey: ["me-steps"] });
  }
  if (t === "roadmap.changed") {
    qc.invalidateQueries({ queryKey: ["roadmap", workspaceId] });
  }
  if (t === "campaign.brain.proposed") {
    const cId = (msg.payload as { campaignId?: string } | undefined)
      ?.campaignId;
    if (cId) {
      qc.invalidateQueries({ queryKey: ["gameplan-coach", workspaceId, cId] });
      qc.invalidateQueries({ queryKey: ["gameplan", workspaceId, cId] });
      qc.invalidateQueries({ queryKey: ["gameplan-context", workspaceId, cId] });
    }
  }
  if (t === "campaign.context.appended") {
    // Targeted invalidation — the detail page subscribes to its own
    // campaign's context query; refetching just that key is enough.
    const cId = (msg.payload as { campaignId?: string } | undefined)
      ?.campaignId;
    if (cId) {
      qc.invalidateQueries({ queryKey: ["gameplan-context", workspaceId, cId] });
      qc.invalidateQueries({ queryKey: ["gameplan", workspaceId, cId] });
    }
    qc.invalidateQueries({ queryKey: ["gameplans", workspaceId] });
  }
}

function mapState(s: WsState): ConnectionStatus {
  return s;
}

function ensureEntry(workspaceId: string, qc: QueryClient): RegistryEntry {
  const existing = registry.get(workspaceId);
  if (existing) return existing;
  const client = createWsClient(`/api/ws/workspaces/${workspaceId}`);
  const fanout = new Set<EventListener>();
  const statusListeners = new Set<(s: ConnectionStatus) => void>();
  const off = client.on((msg) => {
    fanoutEvent(workspaceId, qc, msg);
    fanout.forEach((l) => {
      try {
        l(msg);
      } catch {
        /* swallow listener errors */
      }
    });
  });
  const entry: RegistryEntry = {
    client,
    refCount: 0,
    closeTimer: null,
    fanout,
    off,
    status: mapState(client.state()),
    statusListeners,
    pollTimer: null,
  };
  // Poll the underlying client state and notify listeners on change.
  entry.pollTimer = setInterval(() => {
    const next = mapState(client.state());
    if (next !== entry.status) {
      entry.status = next;
      entry.statusListeners.forEach((l) => l(next));
    }
  }, STATUS_POLL_MS);
  client.connect();
  registry.set(workspaceId, entry);
  return entry;
}

function teardown(workspaceId: string) {
  const entry = registry.get(workspaceId);
  if (!entry) return;
  entry.off();
  if (entry.pollTimer) clearInterval(entry.pollTimer);
  entry.client.disconnect();
  registry.delete(workspaceId);
}

export function useWorkspaceEvents(
  workspaceId: string | null | undefined,
  onEvent: EventListener,
): { connectionStatus: ConnectionStatus } {
  const qc = useQueryClient();
  const [status, setStatus] = useState<ConnectionStatus>(() => {
    if (!workspaceId) return "closed";
    const e = registry.get(workspaceId);
    return e ? e.status : "connecting";
  });

  useEffect(() => {
    if (!workspaceId) {
      setStatus("closed");
      return;
    }
    const entry = ensureEntry(workspaceId, qc);
    if (entry.closeTimer) {
      clearTimeout(entry.closeTimer);
      entry.closeTimer = null;
    }
    entry.refCount += 1;
    entry.fanout.add(onEvent);
    const onStatus = (s: ConnectionStatus) => setStatus(s);
    entry.statusListeners.add(onStatus);
    setStatus(entry.status);

    return () => {
      entry.fanout.delete(onEvent);
      entry.statusListeners.delete(onStatus);
      entry.refCount -= 1;
      if (entry.refCount <= 0) {
        entry.closeTimer = setTimeout(() => {
          // Re-check; another subscriber may have arrived.
          const cur = registry.get(workspaceId);
          if (!cur || cur.refCount > 0) return;
          teardown(workspaceId);
        }, CLOSE_GRACE_MS);
      }
    };
  }, [workspaceId, qc, onEvent]);

  return { connectionStatus: status };
}

// Test-only helpers (named with __ prefix to mark as internal).
export function __resetWorkspaceEventsRegistryForTests(): void {
  for (const [, entry] of registry) {
    if (entry.closeTimer) clearTimeout(entry.closeTimer);
    if (entry.pollTimer) clearInterval(entry.pollTimer);
    try {
      entry.off();
    } catch {
      /* noop */
    }
    try {
      entry.client.disconnect();
    } catch {
      /* noop */
    }
  }
  registry.clear();
}

export function __getWorkspaceEventsEntryForTests(
  workspaceId: string,
): RegistryEntry | undefined {
  return registry.get(workspaceId);
}
